import { useCallback, useEffect, useState } from 'react';
import { ArrowUpCircle, CalendarClock, ExternalLink, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import { fechaExacta } from '../ui/tiempo';
import { Corona } from '../experiencia/MarcaDePlan';
import { olvidarModulosEnConsulta } from '../escala/modulosDeLaCuenta';
import {
  PINTA_COLOR, cancelaAlFinDelPeriodo, conPeriodoPagado, consecuenciaDeCancelar, dinero,
  escaleraRige, estadoVisible, fechaLarga, nombreDePlan, olvidarEstadoDeLaEscalera,
  planesParaMejorar, precioDeLista, textoDeCancelacionPedida, textoDelPaso,
} from './suscripcion';

/* =====================================================================
   Tu suscripción · Ajustes → Suscripción · Programa Peaje U-04
   =====================================================================
   Arriba de la pantalla de siempre: el plan que rige, su estado con su
   color, lo pactado y el próximo cargo con su fecha; si hay mora, cuántos
   días, qué sigue y cuándo, y el enlace para pagar. Dos acciones:
   «Mejorar plan» y «Cancelar al fin del periodo» (o «Seguir con mi plan»
   si la cancelación ya está pedida). «Cambiar método de pago» llega con
   U-05 y aquí no se ve (regla de la fachada).
   En mora no hay periodo pagado que esperar: el botón dice «Pasar al plan
   {inicial}» y la confirmación lo dice así (H-4, vuelta 2 de U-04).
   Con la escalera de mora APAGADA (`escalera` distinto de `true`, también
   si el servidor no lo manda): ni siguiente paso ni días de mora, y
   cancelar es inmediato aunque queden días pagados (vuelta 6).
   `alLeerEstado(conEstado)` le dice a la pantalla de arriba si la tarjeta
   tiene el estado de la nube: con él, la lista de planes es UNA, la de
   aquí; sin él, la pantalla enseña la suya (N-4).
   De dónde sale cada dato: ver `./suscripcion.js`.
   ===================================================================== */
const MORA = new Set(['VENCIDO', 'CONSULTA']);
const CON_PLAN_DE_PAGO = new Set(['AL_CORRIENTE', 'VENCIDO', 'CONSULTA']);

export default function TarjetaDeSuscripcion({ alLeerEstado } = {}) {
  const { tieneNivel, puede } = useAuth();
  const esDueno = Boolean(tieneNivel?.('DUENO'));
  const puedeCancelar = Boolean(puede?.('suscripcion.cancelar')) || esDueno;

  const [esc, setEsc] = useState(null);           // la escalera (peaje)
  const [cuenta, setCuenta] = useState(null);     // lo pactado (escala)
  const [pedibles, setPedibles] = useState(null); // claves de /api/planes
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);
  const [errorAccion, setErrorAccion] = useState(null);
  const [mejorar, setMejorar] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [e, c, p] = await Promise.all([
        api.get('/peaje/suscripcion'),
        // Lo pactado y la lista son un complemento: si no llegan, la
        // tarjeta dice el estado y no pinta ningún precio.
        api.get('/escala/suscripcion').catch(() => null),
        api.get('/planes').catch(() => null),
      ]);
      setEsc(e || {});
      setCuenta(c);
      setPedibles(Array.isArray(p) ? p.map((x) => x.clave) : null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const conEstado = Boolean(estadoVisible(esc));
  const listo = !cargando || Boolean(esc) || Boolean(error);
  useEffect(() => {
    if (listo && alLeerEstado) alLeerEstado(conEstado);
  }, [listo, conEstado, alLeerEstado]);

  function despuesDePedir(texto) {
    setAviso(texto);
    setErrorAccion(null);
    olvidarEstadoDeLaEscalera();
    olvidarModulosEnConsulta();
    cargar();
  }

  async function pedirPlan(plan, ciclo) {
    setOcupado(`${plan.clave}:${ciclo}`); setErrorAccion(null); setAviso(null);
    try {
      await api.post('/suscripcion/cambiar-plan', { plan: plan.clave, ciclo });
      setMejorar(false);
      despuesDePedir(`Queda pedido el plan ${plan.nombre}. El enlace para pagarlo aparece en «Pagos», `
        + 'más abajo; el plan se enciende al confirmarse el pago.');
    } catch (e) {
      setErrorAccion(e.message || 'No se pudo pedir el plan. Vuelve a intentarlo.');
    } finally {
      setOcupado(null);
    }
  }

  async function seguir() {
    setOcupado('seguir'); setErrorAccion(null); setAviso(null);
    try {
      const r = await api.post('/peaje/suscripcion/seguir', {});
      despuesDePedir(r?.dice || 'Queda pedido: tu plan sigue igual.');
    } catch (e) {
      setErrorAccion(e.message || 'No se pudo pedir. Vuelve a intentarlo.');
    } finally {
      setOcupado(null);
    }
  }

  async function cancelar() {
    const r = await api.post('/peaje/suscripcion/cancelar', {});
    // El texto se arma con `efectivo_desde`, no con `r.dice` (H-4).
    despuesDePedir(textoDeCancelacionPedida(r, esc,
      nombreDePlan(cuenta?.lista || null, esc?.plan_inicial) || 'Gratis'));
  }

  if (cargando && !esc && !error) {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3" aria-busy="true">
        <h2 className="font-semibold text-gray-900">Tu suscripción</h2>
        <Cargando filas={3} etiqueta="Cargando tu suscripción" />
      </section>
    );
  }
  if (error && !esc) {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="font-semibold text-gray-900">Tu suscripción</h2>
        <ErrorConReintento que="No se pudo leer el estado de tu suscripción" porque={error}
                           onReintentar={cargar} ocupado={cargando} />
      </section>
    );
  }

  const lista = cuenta?.lista || null;
  const estado = estadoVisible(esc);

  // ESTADO VACÍO: la nube no ha mandado nada a este servidor.
  if (!estado) {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="font-semibold text-gray-900">Tu suscripción</h2>
        <Ayuda id="peaje.suscripcion.pantalla" />
        <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center space-y-2">
          <p className="font-semibold text-gray-700">El estado de tu plan llega con la siguiente conexión a TEGO Cloud</p>
          <p className="text-sm text-gray-600">Aquí vas a ver tu plan, si está al corriente y la fecha de tu próximo cargo.</p>
          <button type="button" onClick={cargar} disabled={cargando}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 disabled:opacity-60">
            {cargando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
            Volver a revisar
          </button>
        </div>
      </section>
    );
  }

  const nombrePlan = nombreDePlan(lista, esc.plan) || 'sin nombre';
  const nombreInicial = nombreDePlan(lista, esc.plan_inicial) || 'Gratis';
  const moneda = lista?.moneda || 'MXN';
  const enMora = MORA.has(esc.estado);
  const lineas = Array.isArray(cuenta?.lineas_pendientes) ? cuenta.lineas_pendientes : [];
  const conEnlace = lineas.find((l) => l?.enlace) || null;
  const pactado = dinero(cuenta?.precio_pactado, moneda);
  const proximo = dinero(cuenta?.proximo_cargo, moneda);
  const ciclo = cuenta?.ciclo || esc.ciclo;
  const rige = escaleraRige(esc);
  const paso = rige ? textoDelPaso(esc.proximo_paso, nombreInicial) : null;
  const opciones = planesParaMejorar(lista, esc.plan, pedibles);
  const puedeProgramarCancelacion = CON_PLAN_DE_PAGO.has(esc.estado)
    && esc.plan && esc.plan !== esc.plan_inicial && !esc.cancela_el;
  const alFinDelPeriodo = cancelaAlFinDelPeriodo(esc);
  const inmediataConDiasPagados = conPeriodoPagado(esc) && !rige;
  const etiquetaCancelar = alFinDelPeriodo ? 'Cancelar al fin del periodo' : `Pasar al plan ${nombreInicial}`;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-4" aria-labelledby="peaje-tu-suscripcion">
      <div className="space-y-2">
        <h2 id="peaje-tu-suscripcion" className="font-semibold text-gray-900">Tu suscripción</h2>
        <Ayuda id="peaje.suscripcion.pantalla" />
      </div>

      {/* Lo importante arriba: el plan y su estado, en grande. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 break-words text-2xl font-bold text-gray-900">Plan {nombrePlan}</span>
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold ${PINTA_COLOR[estado.color] || PINTA_COLOR.amarillo}`}
              data-estado={esc.estado}>
          {estado.texto}
        </span>
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        {pactado && (
          <Dato etiqueta="Precio pactado">
            {pactado} {ciclo === 'anual' ? 'al año' : 'al mes'}
            {cuenta?.descuento?.nombre && (
              <span className="block text-xs text-gray-600">
                Con el descuento «{cuenta.descuento.nombre}»
                {Number.isInteger(cuenta.descuento.periodos_restantes)
                  ? `: le quedan ${cuenta.descuento.periodos_restantes} ${cuenta.descuento.periodos_restantes === 1 ? 'periodo' : 'periodos'}`
                  : ''}.
              </span>
            )}
          </Dato>
        )}
        {esc.estado === 'AL_CORRIENTE' && esc.proximo_paso === 'CARGO' && (
          <Dato etiqueta="Próximo cargo">
            {proximo ? `${proximo} el ` : 'El '}{fechaLarga(esc.proximo_paso_en) || 'día que fije TEGO Cloud'}
          </Dato>
        )}
        {(esc.estado === 'GRATIS' || esc.estado === 'CANCELADO') && (
          <Dato etiqueta="Próximo cargo">Ninguno: el plan {nombrePlan} no tiene cargos.</Dato>
        )}
        {enMora && rige && Number.isInteger(esc.dias_mora) && (
          <Dato etiqueta="Días de mora">
            {esc.dias_mora} {esc.dias_mora === 1 ? 'día' : 'días'} sin pagar
          </Dato>
        )}
        {enMora && rige && esc.pausa_hasta && (
          <Dato etiqueta="Prórroga">
            Hasta el {fechaLarga(esc.pausa_hasta)}: todo sigue funcionando igual.
          </Dato>
        )}
        {enMora && rige && !esc.pausa_hasta && paso && esc.proximo_paso_en && (
          <Dato etiqueta="Siguiente paso">
            El {fechaLarga(esc.proximo_paso_en)}, {paso}.
          </Dato>
        )}
        {esc.cancela_el && (
          <Dato etiqueta="Cambio pedido">
            Tu plan {nombrePlan} sigue hasta el {fechaLarga(esc.cancela_el)}
            {esc.proximo_paso_en ? `; el cambio rige desde el ${fechaLarga(esc.proximo_paso_en)}` : ''}.
          </Dato>
        )}
      </dl>

      {esc.estado === 'CONSULTA' && (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="status">
          Lo que el plan {nombreInicial} no trae está en consulta: se ve todo y nada se borró, pero no se registra nada nuevo. Al pagar se vuelve a abrir.
        </p>
      )}
      {esc.estado === 'PEDIDO' && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="status">
          Pediste un plan: se enciende en cuanto se confirma el pago.
        </p>
      )}

      {(enMora || esc.estado === 'PEDIDO') && (
        conEnlace ? (
          <a href={conEnlace.enlace} target="_blank" rel="noreferrer"
             className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2.5 text-sm font-semibold text-white sm:w-auto">
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Pagar ahora{dinero(conEnlace.monto, conEnlace.moneda || moneda) ? ` · ${dinero(conEnlace.monto, conEnlace.moneda || moneda)}` : ''}
          </a>
        ) : (
          <p className="text-sm text-gray-700">
            Para recibir el enlace de pago: {cuenta?.contacto || 'escribe a tu proveedor de TEGO.'}
          </p>
        )
      )}

      {aviso && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900" role="status">{aviso}</p>
      )}
      {errorAccion && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800" role="alert">{errorAccion}</p>
      )}

      {/* Las acciones: anchas en el teléfono, en fila desde 640 px. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {opciones.length > 0 && (
          <div className="flex flex-col gap-1">
            <button type="button" onClick={() => setMejorar((v) => !v)} disabled={!esDueno}
                    aria-expanded={mejorar} aria-describedby={!esDueno ? 'peaje-mejorar-porque' : undefined}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto">
              <ArrowUpCircle className="h-4 w-4" aria-hidden="true" /> Mejorar plan
            </button>
            {!esDueno && (
              <p id="peaje-mejorar-porque" className="text-xs text-gray-600">Lo pide el dueño de la cuenta.</p>
            )}
          </div>
        )}
        {puedeProgramarCancelacion && (
          <div className="flex flex-col gap-1">
            <button type="button" onClick={() => setConfirmarCancelar(true)} disabled={!puedeCancelar}
                    aria-describedby={!puedeCancelar ? 'peaje-cancelar-porque' : undefined}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 disabled:opacity-50 sm:w-auto">
              <XCircle className="h-4 w-4" aria-hidden="true" /> {etiquetaCancelar}
            </button>
            {!puedeCancelar && (
              <p id="peaje-cancelar-porque" className="text-xs text-gray-600">Lo pide el dueño de la cuenta.</p>
            )}
            {alFinDelPeriodo && <Ayuda id="peaje.suscripcion.cancelar" />}
            {inmediataConDiasPagados && <Ayuda id="peaje.suscripcion.cancelar_inmediata" />}
            {!alFinDelPeriodo && !inmediataConDiasPagados && <Ayuda id="peaje.suscripcion.cancelar_en_mora" />}
          </div>
        )}
        {esc.cancela_el && (
          <div className="flex flex-col gap-1">
            <button type="button" onClick={seguir} disabled={!puedeCancelar || ocupado === 'seguir'}
                    aria-describedby={!puedeCancelar ? 'peaje-seguir-porque' : undefined}
                    className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto">
              {ocupado === 'seguir' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <CalendarClock className="h-4 w-4" aria-hidden="true" />}
              Seguir con mi plan
            </button>
            {!puedeCancelar && (
              <p id="peaje-seguir-porque" className="text-xs text-gray-600">Lo pide el dueño de la cuenta.</p>
            )}
          </div>
        )}
      </div>

      {mejorar && esDueno && (
        <div className="space-y-2" id="peaje-mejorar">
          <h3 className="text-xs font-semibold uppercase text-gray-500">Planes con más</h3>
          <Ayuda id="peaje.suscripcion.mejorar" />
          <ul className="space-y-2">
            {opciones.map((p) => {
              const mensual = dinero(precioDeLista(p, 'mensual'), moneda);
              const anual = dinero(precioDeLista(p, 'anual'), moneda);
              return (
                <li key={p.clave} className="rounded-xl border border-gray-200 p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[var(--color-marca)]"><Corona /></span>
                    <span className="font-semibold text-gray-900">{p.nombre}</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {mensual ? `${mensual} al mes` : ''}
                    {mensual && anual ? ' · ' : ''}
                    {anual ? `${anual} al año` : ''}
                  </p>
                  {Array.isArray(p.incluye) && p.incluye.length > 0 && (
                    <p className="text-xs text-gray-600">{p.incluye.join(' · ')}</p>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    {mensual && (
                      <button type="button" onClick={() => pedirPlan(p, 'mensual')} disabled={Boolean(ocupado)}
                              className="min-h-11 w-full rounded-lg bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto">
                        {ocupado === `${p.clave}:mensual` ? 'Pidiendo…' : 'Pedir al mes'}
                      </button>
                    )}
                    {anual && (
                      <button type="button" onClick={() => pedirPlan(p, 'anual')} disabled={Boolean(ocupado)}
                              className="min-h-11 w-full rounded-lg border border-[var(--color-marca)] bg-white px-3 py-2 text-sm font-semibold text-[var(--color-marca)] disabled:opacity-60 sm:w-auto">
                        {ocupado === `${p.clave}:anual` ? 'Pidiendo…' : 'Pedir al año'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {esc.leido_en && (
        <p className="text-xs text-gray-500">Según TEGO Cloud, {fechaExacta(esc.leido_en)}.</p>
      )}

      {confirmarCancelar && (
        <ModalConfirmar
          titulo={etiquetaCancelar}
          descripcion={consecuenciaDeCancelar(esc, nombrePlan, nombreInicial)}
          etiquetaConfirmar={alFinDelPeriodo ? 'Sí, cancelar al fin del periodo' : `Sí, pasar al plan ${nombreInicial}`}
          peligro
          onConfirmar={cancelar}
          onCerrar={() => setConfirmarCancelar(false)}
        />
      )}
    </section>
  );
}

function Dato({ etiqueta, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase text-gray-500">{etiqueta}</dt>
      <dd className="break-words text-gray-900">{children}</dd>
    </div>
  );
}
